// @ts-nocheck
import {
  VictoryAccessibleGroup,
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryLabel,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip
} from 'victory'
import { getMonth, getSeason, getSymbol } from 'utils'
import { ToolTipLabel } from '../../components/ToolTipLabel'
import LegendButton from '../../components/LegendButton'
import {
  CleanedChartDataType,
  MonthlyCompareRainfallType
} from '../../interfaces'
import { useState, useEffect, useId } from 'react'

// bitmask values
const YEAR_1 = 1 //00000001
const YEAR_2 = 2 //00000010
const YEAR_3 = 4 //00000100
const YEAR_4 = 8 //00001000
const AVERAGE = 16 //00010000
const YEAR: Array<number> = [YEAR_1, YEAR_2, YEAR_3, YEAR_4, AVERAGE]

const Monthly = 'monthlyData'
const YearToDate = 'yearToDateData'
const Seasonal = 'seasonalData'
const Titles: { [index: string]: any } = {
  monthlyData: 'Monthly rainfall',
  yearToDateData: 'Year to date',
  seasonalData: 'Seasonal rainfall'
}

const colorArray: Array<string> = [
  '#00A698',
  '#EE0297',
  '#A20017',
  '#407629',
  'rgba(10, 192, 221, 0.15)'
]

const MonthlyCompareRainfall = ({
  data,
  width = 762
}: MonthlyCompareRainfallType) => {
  //show average
  const [displayAvg, setDisplayAvg] = useState(true)
  //set the selection via the radio buttons
  const [selected, setSelected] = useState(Monthly)
  // labels for legend
  const [label, setLabel] = useState<
    Array<{
      name: string
      symbol: {
        fill: string
        type: string
      }
    }>
  >()
  // colors for graph
  const [colors, setColors] = useState(colorArray)
  // colors for legend
  const [legendColors, setLegendColors] = useState(colorArray)
  // symbols for scatter
  const [symbol, setSymbol] = useState<Record<string, any>>()
  // data for graph
  const [filtered, setFiltered] = useState<Array<any>>([])
  // data for bar chart
  const [avg, setAvg] = useState<CleanedChartDataType>([])
  // bitmask - boolean on/ off per year
  const [mask, setMask] = useState(YEAR_1 | YEAR_2 | YEAR_3 | YEAR_4 | AVERAGE)

  // filters data based on bitmask
  useEffect(() => {
    if (data && selected && mask) {
      const currentData = data[selected] ? data[selected] : []
      setFiltered(currentData.filter((value, index) => mask & (1 << index)))
    }
  }, [data, mask, selected, setFiltered])
  // finds avgerage for bar chart, dependant on bitmask
  useEffect(() => {
    if (data && data[selected]) {
      if (selected === Seasonal) {
        setAvg(data.seasonalAverageData)
      } else if (selected === YearToDate) {
        setAvg(data.yearToDateAverageData)
      } else {
        setAvg(data.monthlyAverageData)
      }
    }
  }, [data, selected, setAvg])
  // filters colors based on bitmask
  useEffect(() => {
    if (mask && setColors) {
      setColors(colorArray.filter((value, index) => mask & (1 << index)))
    }
  }, [mask, setColors])
  // changes colors based on bitmask
  useEffect(() => {
    if (colorArray) {
      setLegendColors(
        colorArray.map((value, index) =>
          mask & (1 << index) ? value : '#6E6E6E'
        )
      )
    }
  }, [colorArray, mask, setLegendColors])
  // sets up data for legend labels
  useEffect(() => {
    if (data && selected && !label) {
      const currentData = data[selected] ? data[selected] : []
      if (currentData.length > 0) {
        const sym: Record<string, any> = {}
        const labelData = data[selected].map((y, i) => {
          if (y && y.Data && y.Year) {
            const s = getSymbol(i)
            sym[`${y.Year}`] = s
            return {
              name: `${y.Year}`,
              symbol: { type: s }
            }
          } else {
            return { name: `- - -` }
          }
        })
        setSymbol(sym)
        setLabel([
          ...labelData,
          {
            name: 'Average',
            symbol: {
              fill: 'rgba(10, 192, 221, 0.15)',
              type: 'square'
            }
          }
        ])
      }
    }
  }, [data, selected])
  const id = useId()
  return (
    <div className="wc-rainfall__chart">
      <VictoryChart
        desc='monthly rainfall comparison graph'
        theme={VictoryTheme.material}
        domainPadding={{
          x: selected === Seasonal ? [25, 25] : [10, 10]
        }}
        padding={{
          left: 90,
          right: 10,
          top: 40,
          bottom: width > 700 ? 160 : width > 600 ? 120 : 140
        }}
        width={width}
        height={width > 700 ? 460 : width > 600 ? 480 : 450}
        style={{
          parent: {
            width,
            height: width > 700 ? 460 : width > 600 ? 520 : 550
          }
        }}
      >
        <VictoryBar
          // @ts-ignore
          data={displayAvg ? avg : []}
          cornerRadius={{ top: 5 }}
          style={{
            data: {
              fill: '#0AC0DD',
              fillOpacity: 0.15,
              stroke: 'transparent'
            }
          }}
        />
        {/* lines from data */}
        <VictoryGroup
          groupComponent={<VictoryAccessibleGroup aria-label="monthly rainfall data points" />}
        >
          {filtered.map((d, i) => (
            <VictoryLine
              key={'line' + i}
              data={d.Data}
              style={{
                data: {
                  width: 2,
                  stroke: colors[i],
                  strokeWidth: () => (i === 0 ? 4 : 1)
                }
              }}
            />
          ))}
          {symbol &&
            filtered.map((d, i) => (
              <VictoryScatter
                key={'scatter' + i}
                data={d.Data}
                size={5}
                symbol={symbol[`${d.Year}`]}
                style={{
                  data: {
                    fill: colors[i]
                  }
                }}
                labels={(l) => l}
                minBubbleSize={8}
                labelComponent={
                  <VictoryTooltip
                    cornerRadius={4}
                    orientation="bottom"
                    pointerOrientation="bottom"
                    flyoutWidth={120}
                    flyoutHeight={50}
                    pointerWidth={120}
                    height={75}
                    width={selected === Seasonal ? 140 : 110}
                    flyoutStyle={{
                      fill: '#ffffff',
                      stroke: (d) => colors[i]
                    }}
                    labelComponent={
                      <ToolTipLabel
                        year={d.Year}
                        color={colors[i]}
                        unit=" mm"
                        isSeasonal={selected === Seasonal}
                      />
                    }
                  />
                }
              />
            ))}
        </VictoryGroup>
        {/* x axis */}
        <VictoryAxis
          orientation="bottom"
          width={width}
          tickValues={
            selected === Monthly || selected === YearToDate
              ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]
              : [0, 1, 2, 3, 4, 5]
          }
          tickFormat={(x) => {
            if (x < 0) return ""
            if (selected === Seasonal) {
              const s = getSeason(Number(x))
              if (s) {
                return width > 600 ? s : s?.substring(0, 3)
              } else {
                return ''
              }
            }
            return width > 600 ? getMonth(Number(x)) : (getMonth(Number(x))?.slice(0, 1) ?? "")
          }}
          style={{
            axis: {
              stroke: '#C7C7C7',
              strokeWidth: 1,
              strokeLength: 2
            },
            axisLabel: {
              fontSize: 16,
              fontFamily: 'Arial'
            },
            grid: {
              stroke: 'transparent',
              strokeWidth: 0,
              strokeLength: 0
            },
            ticks: { stroke: 'transparent' }
          }}
          fixLabelOverlap
          tickLabelComponent={
            <VictoryLabel
              id={(i) => `x:${id}:${i.index}`}
              dy={10}
              style={{
                fontSize: 16,
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fill: '#6E6E6E'
              }}
            />
          }
        />
        {/* y axis */}
        {data && data[selected] && (
          <VictoryAxis
            dependentAxis
            tickFormat={(x) => `${x > 0 ? Math.round(x) : 0} mm`}
            tickCount={6}
            style={{
              axis: { stroke: 'transparent' },
              axisLabel: {
                fontSize: 16,
                fontFamily: 'Arial'
              },
              grid: {
                stroke: '#C7C7C7',
                strokeWidth: 1,
                strokeLength: 2
              },
              ticks: { stroke: 'transparent' },
              tickLabels: {
                fontSize: 16,
                fill: '#6E6E6E',
                fontFamily: 'Arial'
              }
            }}
            tickLabelComponent={
              <VictoryLabel
                id={(i) => `x:${id}:${i.index}`}
                style={{
                  fontSize: 16,
                  fill: '#6E6E6E',
                  fontFamily: 'Arial',
                  fontWeight: 'bold'
                }}
              />
            }
          />
        )}
        <VictoryLegend
          x={
            width > 600
              ? width / 2 - 260
              : 0
          }
          y={width > 700 ? 400 : width > 600 ? 460 : 370}
          orientation={"horizontal"}
          itemsPerRow={width > 600 ? 6 : 3}
          gutter={60}
          rowGutter={{ bottom: 35 }}
          style={{ border: { stroke: 'transparent' } }}
          colorScale={legendColors}
          data={label}
          labelComponent={
            <VictoryLabel
              id={(i) => `ledg:${id}:${i.index}`}
              className="clickable"
              style={{
                fontSize: 16,
                fill: '#6E6E6E',
                fontFamily: 'Arial',
                fontWeight: 'bold'
              }}
              backgroundStyle={{
                fill: 'transparent',
                stroke: (d) => {
                  if (label) {
                    const names = label.map((x) => x.name)
                    if (d?.datum?.name === 'Average') {
                      return !displayAvg ? '#C7C7C7' : '#007497'
                    } else {
                      const index = names.indexOf(d?.datum?.name)
                      //console.log(names, index)
                      return legendColors[index] === '#6E6E6E'
                        ? '#C7C7C7'
                        : '#007497'
                    }
                  }
                  return '#007497'
                },
                strokeWidth: '2',
                // @ts-ignore
                rx: '4'
              }}
              backgroundPadding={[{ top: 10, right: 20, bottom: 10, left: 30 }]}
              dy={-10}
            />
          }
          events={[
            {
              target: 'labels',
              eventHandlers: {
                onClick: () => {
                  return [
                    {
                      target: 'data',
                      mutation: (props) => {
                        if (
                          props.datum &&
                          props.datum.name &&
                          props.datum.name === 'Average'
                        ) {
                          setDisplayAvg(!displayAvg)
                        }
                        const fill = props.style && props.style.fill
                        if (fill === '#6E6E6E') {
                          setMask(YEAR[props.index] | mask)
                        } else {
                          setMask(mask & ~YEAR[props.index])
                        }
                      }
                    }
                  ]
                }
              }
            }
          ]}
        />
      </VictoryChart>

      <hr />
      <div className="wc-rainfall__legend relative">
        <LegendButton
          text={Titles.monthlyData}
          checked={selected === Monthly}
          handleChange={() => {
            setSelected(Monthly)
            setTimeout(() => {
              setDisplayAvg(true)
              setMask(YEAR_1 | YEAR_2 | YEAR_3 | YEAR_4 | AVERAGE)
            }, 800)
          }}
        />
        <LegendButton
          text={Titles.yearToDateData}
          checked={selected === YearToDate}
          handleChange={() => {
            setSelected(YearToDate)
            setTimeout(() => {
              setDisplayAvg(true)
              setMask(YEAR_1 | YEAR_2 | YEAR_3 | YEAR_4 | AVERAGE)
            }, 800)
          }}
        />
        <LegendButton
          text={Titles.seasonalData}
          checked={selected === Seasonal}
          handleChange={() => {
            setSelected(Seasonal)
            setTimeout(() => {
              setDisplayAvg(true)
              setMask(YEAR_1 | YEAR_2 | YEAR_3 | YEAR_4 | AVERAGE)
            }, 800)
          }}
        />
      </div>
    </div>
  )
}

export default MonthlyCompareRainfall
